/**
 * Header row for the current NCCN guideline.
 *
 * Shows the "NCCN Guideline" label and guideline-level AI result badge on the
 * left, with the guideline position (`NN/NN`) badge and total-guidelines count
 * pushed to the right. Navigation is driven by the shared footer controls, not
 * this row.
 * @module GuidelineHeaderRow
 */
import { Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import React from "react";
import { EvaluationResult } from "../../../data-model/nccnGuidelines";
import { GuidelineResultBadge } from "./nccnVerdictBadge";

/** Props for {@link GuidelineHeaderRow}. */
interface GuidelineHeaderRowProps {
  /** 1-based position for the `NN/NN` progress badge (already resolved). */
  position: number;
  /** Denominator for the `NN/NN` progress badge. */
  total: number;
  /** Full order guideline count, shown in the "N Total Guidelines" tag. */
  orderTotal: number;
  /** Guideline-level AI result. */
  result: EvaluationResult | null | undefined;
}

/**
 * Renders the guideline label, AI result badge, position badge, and total.
 *
 * The `NN/NN` badge and the "N Total Guidelines" tag are independent: the badge
 * reflects the reviewer's progress through the guidelines being stepped
 * through, while the tag always shows the full order count.
 *
 * @param props - Resolved position/total for the badge, the full order count,
 *   and the guideline result
 * @returns The rendered header row
 */
export const GuidelineHeaderRow: React.FC<GuidelineHeaderRowProps> = ({
  position,
  total,
  orderTotal,
  result,
}) => {
  const current = Math.min(Math.max(position, 1), total);
  const positionLabel = `${String(current).padStart(
    String(total).length,
    "0",
  )}/${total}`;

  return (
    <div className="flex w-full items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="line-height-[22px] text-sm font-semibold text-[#0F0F0F]">
          NCCN Guideline
        </span>
        <GuidelineResultBadge result={result} />
      </div>
      <div className="flex items-center gap-2">
        <Tags
          label={positionLabel}
          backgroundColor="#F5F5F5"
          textColor="#374151"
        />
        <Tags
          label={`${orderTotal} Total Guidelines`}
          backgroundColor="#EAF2FF"
          textColor="#0056D6"
        />
      </div>
    </div>
  );
};
